<template>
  <main-layout>
    <template #header>
      <main-header :title="'출력량 현황' | translate">
        <template #append-title>
          <menu-beta-badge />
        </template>
      </main-header>
    </template>
    <template #toolbar>
      <div class="pv-toolbar">
        <select v-model="search.rentalcompanyId" class="pv-control" @change="reload">
          <option :value="null">{{ '전체 임대업체' | translate }}</option>
          <option
            v-for="item in rentalcompanyOptions"
            :key="item.rentalcompanyId"
            :value="item.rentalcompanyId"
          >
            {{ item.rentalcompanyName }}
          </option>
        </select>
        <div class="preset-group">
          <button
            v-for="p in periodPresets"
            :key="p.key"
            type="button"
            class="preset-btn"
            :class="{ on: periodPreset === p.key }"
            @click="applyPeriodPreset(p.key)"
          >
            {{ p.label | translate }}
          </button>
        </div>
        <input v-model="search.fromDate" type="date" class="pv-control" @change="onManualDate" />
        <span>~</span>
        <input v-model="search.toDate" type="date" class="pv-control" @change="onManualDate" />
        <input
          v-model="search.keyword"
          type="text"
          class="pv-control"
          :placeholder="'거래처·시리얼·모델·임대업체' | translate"
          @keyup.enter="reload"
        />
        <button type="button" class="pv-btn" @click="reload">{{ '조회' | translate }}</button>
        <span v-if="search.accountId" class="pv-drill-chip">
          {{ '거래처' | translate }}: {{ search.accountName || search.accountId }}
          <button type="button" class="pv-drill-clear" @click="clearAccountDrill">×</button>
        </span>
      </div>
    </template>
    <template #content>
      <div class="print-volume-page">
        <p class="pv-hint">
          {{
            '기간 출력량은 일별 카운터 델타(A3×2 A4 환산) 합산입니다. 누적출력량은 현재 모니터링 카운터입니다.'
              | translate
          }}
        </p>

        <div class="pv-kpi-row">
          <div class="pv-kpi">
            <div class="label">{{ '기간 A4 출력' | translate }}</div>
            <div class="value">{{ formatNum(summary.periodPagesA4) }}</div>
          </div>
          <div class="pv-kpi">
            <div class="label">{{ '기간 흑백(A4)' | translate }}</div>
            <div class="value">{{ formatNum(summary.periodMonoA4) }}</div>
          </div>
          <div class="pv-kpi">
            <div class="label">{{ '기간 컬러(A4)' | translate }}</div>
            <div class="value">{{ formatNum(summary.periodColorA4) }}</div>
          </div>
          <div class="pv-kpi">
            <div class="label">{{ '프린터수' | translate }}</div>
            <div class="value">{{ formatInt(summary.printerCount) }}</div>
          </div>
          <div class="pv-kpi">
            <div class="label">{{ '거래처수' | translate }}</div>
            <div class="value">{{ formatInt(summary.accountCount) }}</div>
          </div>
          <div class="pv-kpi">
            <div class="label">{{ '누적 A4 합' | translate }}</div>
            <div class="value">{{ formatNum(summary.cumulativePagesA4) }}</div>
          </div>
        </div>

        <div class="pv-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            type="button"
            :class="{ active: activeTab === tab.value }"
            @click="changeTab(tab.value)"
          >
            {{ tab.label | translate }}
          </button>
        </div>

        <template v-if="activeTab !== 'efficiency'">
          <div class="table-wrap">
            <table class="pv-table">
              <thead>
                <tr>
                  <th class="sortable" @click="sortBy('groupLabel')">
                    {{ groupLabel | translate }}{{ sortMark('groupLabel') }}
                  </th>
                  <th v-if="activeTab === 'PRINTER'">{{ '모델' | translate }}</th>
                  <th v-if="activeTab === 'PRINTER'">{{ '거래처' | translate }}</th>
                  <th v-if="activeTab === 'PRINTER'">{{ '임대업체' | translate }}</th>
                  <th class="sortable" @click="sortBy('periodPagesA4')">
                    {{ '기간 A4' | translate }}{{ sortMark('periodPagesA4') }}
                  </th>
                  <th class="sortable" @click="sortBy('periodMonoA4')">
                    {{ '흑백' | translate }}{{ sortMark('periodMonoA4') }}
                  </th>
                  <th class="sortable" @click="sortBy('periodColorA4')">
                    {{ '컬러' | translate }}{{ sortMark('periodColorA4') }}
                  </th>
                  <th class="sortable" @click="sortBy('cumulativePagesA4')">
                    {{ '누적 A4' | translate }}{{ sortMark('cumulativePagesA4') }}
                  </th>
                  <th class="sortable" @click="sortBy('printerCount')">
                    {{ '프린터' | translate }}{{ sortMark('printerCount') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rows.length">
                  <td :colspan="colspan" class="empty">{{ '데이터가 없습니다.' | translate }}</td>
                </tr>
                <tr
                  v-for="(row, idx) in rows"
                  :key="'pv-' + idx"
                  :class="{ 'pv-row-clickable': activeTab === 'ACCOUNT' || activeTab === 'RENTAL' }"
                  :title="rowClickTitle"
                  @click="onRowClick(row)"
                >
                  <td :title="row.groupLabel">
                    <span class="pv-cell-with-icon">
                      <button
                        v-if="activeTab === 'ACCOUNT'"
                        type="button"
                        class="cell-link"
                        :disabled="!row.accountId"
                        :title="'이 거래처 프린터 보기' | translate"
                        @click.stop="drillToPrinters(row)"
                      >
                        {{ row.groupLabel || '-' }}
                      </button>
                      <button
                        v-else-if="activeTab === 'PRINTER'"
                        type="button"
                        class="cell-link"
                        :disabled="!canOpenPrinter(row)"
                        :title="'프린터 상세' | translate"
                        @click.stop="openPrinterDetail(row)"
                      >
                        {{ row.printerSerialNo || row.groupLabel || '-' }}
                      </button>
                      <button
                        v-else
                        type="button"
                        class="cell-link"
                        :disabled="!row.rentalcompanyId"
                        :title="'이 임대업체 거래처 보기' | translate"
                        @click.stop="drillToAccounts(row)"
                      >
                        {{ row.groupLabel || '-' }}
                      </button>
                      <collect-delay-days-chip
                        v-if="activeTab === 'PRINTER'"
                        :datetime="row.uptDatetime"
                      />
                      <print-volume-trend-icon
                        v-if="activeTab === 'ACCOUNT' && row.accountId"
                        @click="openAccountTrend(row)"
                      />
                    </span>
                  </td>
                  <td v-if="activeTab === 'PRINTER'">
                    <span class="pv-cell-with-icon">
                      {{ row.printerModelName || '-' }}
                      <print-volume-trend-icon
                        v-if="row.monitoringPrinterId"
                        @click="openPrinterTrend(row)"
                      />
                    </span>
                  </td>
                  <td v-if="activeTab === 'PRINTER'">
                    <span class="pv-cell-with-icon">
                      <button
                        type="button"
                        class="cell-link"
                        :disabled="!row.accountId"
                        :title="'이 거래처 프린터 보기' | translate"
                        @click.stop="drillToPrinters(row)"
                      >
                        {{ row.accountName || '-' }}
                      </button>
                      <print-volume-trend-icon
                        v-if="row.accountId"
                        @click="openAccountTrend(row)"
                      />
                    </span>
                  </td>
                  <td v-if="activeTab === 'PRINTER'">{{ row.rentalcompanyName || '-' }}</td>
                  <td>{{ formatNum(row.periodPagesA4) }}</td>
                  <td>{{ formatNum(row.periodMonoA4) }}</td>
                  <td>{{ formatNum(row.periodColorA4) }}</td>
                  <td>{{ formatNum(row.cumulativePagesA4) }}</td>
                  <td>{{ formatInt(row.printerCount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pv-paging">
            <button type="button" class="pv-btn outline" :disabled="page <= 1" @click="prevPage">
              {{ '이전' | translate }}
            </button>
            <span>{{ page }} / {{ totalPages }}</span>
            <button
              type="button"
              class="pv-btn outline"
              :disabled="page >= totalPages"
              @click="nextPage"
            >
              {{ '다음' | translate }}
            </button>
          </div>
        </template>

        <supply-efficiency-panel
          v-else
          :base-payload="efficiencyPayload"
          :overview-path="efficiencyOverviewPath"
          :ranking-path="efficiencyRankingPath"
          :cycles-path="efficiencyCyclesPath"
          :include-rental="true"
        />
        <print-volume-trend-drawer
          v-model="printVolumeTrendOpen"
          :trend-path="printVolumeTrendPath"
          :payload="printVolumeTrendPayload"
          :title-extra="printVolumeTrendTitle"
        />
      </div>
    </template>
  </main-layout>
</template>

<script>
import PATH from '@/constants/api'
import SupplyEfficiencyPanel from '@/views/monitoring/supply/SupplyEfficiencyPanel'
import MenuBetaBadge from '@/components/common/MenuBetaBadge.vue'
import CollectDelayDaysChip from '@/views/monitoring/print-volume/CollectDelayDaysChip'
import PrintVolumeTrendIcon from '@/views/monitoring/print-volume/PrintVolumeTrendIcon'
import PrintVolumeTrendDrawer from '@/views/monitoring/print-volume/PrintVolumeTrendDrawer'

function toYmd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultFrom() {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return toYmd(d)
}

export default {
  name: 'PrintVolume',
  components: {
    SupplyEfficiencyPanel,
    MenuBetaBadge,
    CollectDelayDaysChip,
    PrintVolumeTrendIcon,
    PrintVolumeTrendDrawer,
  },
  data() {
    return {
      search: {
        fromDate: defaultFrom(),
        toDate: toYmd(new Date()),
        keyword: '',
        rentalcompanyId: null,
        accountId: null,
        accountName: '',
      },
      rentalcompanyOptions: [],
      periodPreset: '30d',
      periodPresets: [
        { key: '7d', label: '7일', days: 7 },
        { key: '30d', label: '30일', days: 30 },
        { key: '90d', label: '90일', days: 90 },
      ],
      activeTab: 'ACCOUNT',
      summary: {},
      rows: [],
      totalCount: 0,
      page: 1,
      rowsPerPage: 30,
      orderBy: 'periodPagesA4',
      orderDir: 'DESC',
      printVolumeTrendOpen: false,
      printVolumeTrendPayload: {},
      printVolumeTrendTitle: '',
    }
  },
  computed: {
    tabs() {
      return [
        { value: 'ACCOUNT', label: '거래처' },
        { value: 'PRINTER', label: '프린터' },
        { value: 'RENTAL', label: '임대업체' },
        { value: 'efficiency', label: '소모품 효율' },
      ]
    },
    groupLabel() {
      if (this.activeTab === 'PRINTER') return '프린터'
      if (this.activeTab === 'RENTAL') return '임대업체'
      return '거래처'
    },
    colspan() {
      return this.activeTab === 'PRINTER' ? 9 : 6
    },
    totalPages() {
      return Math.max(1, Math.ceil((this.totalCount || 0) / this.rowsPerPage))
    },
    rowClickTitle() {
      if (this.activeTab === 'ACCOUNT') return '이 거래처 프린터 보기'
      if (this.activeTab === 'RENTAL') return '이 임대업체 거래처 보기'
      return null
    },
    efficiencyPayload() {
      return {
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        keyword: this.search.keyword || null,
        rentalcompanyId: this.search.rentalcompanyId,
        accountId: this.search.accountId || null,
      }
    },
    efficiencyOverviewPath() {
      return PATH.MONITORING.SUPPLY_EFFICIENCY_OVERVIEW
    },
    efficiencyRankingPath() {
      return PATH.MONITORING.SUPPLY_EFFICIENCY_RANKING
    },
    efficiencyCyclesPath() {
      return PATH.MONITORING.SUPPLY_EFFICIENCY_CYCLES
    },
    printVolumeTrendPath() {
      return PATH.MONITORING.PRINT_VOLUME_TREND
    },
  },
  mounted() {
    this.loadRentalcompanies()
    this.reload()
  },
  methods: {
    volumePayload() {
      return {
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        keyword: this.search.keyword || null,
        rentalcompanyId: this.search.rentalcompanyId,
        accountId: this.search.accountId || null,
        groupBy: this.activeTab,
        orderBy: this.orderBy,
        orderDir: this.orderDir,
        page: this.page,
        rows: this.rowsPerPage,
      }
    },
    canOpenPrinter(row) {
      return !!(row && row.tnDeviceId && row.rentalcompanyId)
    },
    openPrinterDetail(row) {
      if (!this.canOpenPrinter(row)) return
      this.$router.push({
        name: 'managertnbox.monitoring.device.list.detail.info',
        query: {
          rentalcompanyId: row.rentalcompanyId,
          tnDeviceId: row.tnDeviceId,
          accountId: row.accountId,
        },
      })
    },
    openAccountTrend(row) {
      if (!row?.accountId) return
      this.printVolumeTrendTitle = row.accountName || row.groupLabel || this.$translate('거래처')
      this.printVolumeTrendPayload = {
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        rentalcompanyId: this.search.rentalcompanyId || row.rentalcompanyId || null,
        accountId: row.accountId,
        monitoringPrinterId: null,
      }
      this.printVolumeTrendOpen = true
    },
    openPrinterTrend(row) {
      if (!row?.monitoringPrinterId) return
      this.printVolumeTrendTitle =
        row.printerModelName || row.printerSerialNo || this.$translate('프린터')
      this.printVolumeTrendPayload = {
        fromDate: this.search.fromDate,
        toDate: this.search.toDate,
        rentalcompanyId: this.search.rentalcompanyId || row.rentalcompanyId || null,
        accountId: null,
        monitoringPrinterId: row.monitoringPrinterId,
      }
      this.printVolumeTrendOpen = true
    },
    drillToPrinters(row) {
      if (!row?.accountId) return
      this.search.accountId = row.accountId
      this.search.accountName = row.accountName || row.groupLabel || ''
      if (row.rentalcompanyId) this.search.rentalcompanyId = row.rentalcompanyId
      this.activeTab = 'PRINTER'
      this.orderBy = 'periodPagesA4'
      this.orderDir = 'DESC'
      this.reload()
    },
    drillToAccounts(row) {
      if (!row?.rentalcompanyId) return
      this.search.rentalcompanyId = row.rentalcompanyId
      this.search.accountId = null
      this.search.accountName = ''
      this.activeTab = 'ACCOUNT'
      this.orderBy = 'periodPagesA4'
      this.orderDir = 'DESC'
      this.reload()
    },
    onRowClick(row) {
      if (this.activeTab === 'ACCOUNT') this.drillToPrinters(row)
      else if (this.activeTab === 'RENTAL') this.drillToAccounts(row)
    },
    clearAccountDrill() {
      this.search.accountId = null
      this.search.accountName = ''
      this.reload()
    },
    async loadRentalcompanies() {
      try {
        const res = await this.$request(PATH.MONITORING.RENTALCOMPANY_LIST)
          .post()
          .enqueue()
        const data = res?.data || res || []
        this.rentalcompanyOptions = Array.isArray(data) ? data : []
      } catch (e) {
        console.error(e)
        this.rentalcompanyOptions = []
      }
    },
    async reload() {
      this.page = 1
      if (this.activeTab === 'efficiency') {
        await this.loadSummary()
        return
      }
      await this.loadList()
    },
    async loadSummary() {
      try {
        const res = await this.$request(PATH.MONITORING.PRINT_VOLUME_SUMMARY)
          .setObject(this.volumePayload())
          .enqueue()
        this.summary = res?.data || res || {}
      } catch (e) {
        console.error(e)
        this.summary = {}
      }
    },
    async loadList() {
      try {
        const res = await this.$request(PATH.MONITORING.PRINT_VOLUME_LIST)
          .setObject(this.volumePayload())
          .enqueue()
        const data = res?.data || res || {}
        this.rows = Array.isArray(data.list) ? data.list : []
        this.totalCount = Number(data.totalCount || 0)
        if (data.summary) this.summary = data.summary
      } catch (e) {
        console.error(e)
        this.rows = []
        this.totalCount = 0
      }
    },
    changeTab(v) {
      this.activeTab = v
      this.page = 1
      this.orderBy = v === 'efficiency' ? 'pagesPerCycleA4' : 'periodPagesA4'
      this.orderDir = 'DESC'
      if (v === 'efficiency') {
        this.loadSummary()
      } else {
        this.reload()
      }
    },
    applyPeriodPreset(key) {
      const p = this.periodPresets.find((x) => x.key === key)
      if (!p) return
      this.periodPreset = key
      const to = new Date()
      const from = new Date()
      from.setDate(from.getDate() - p.days)
      this.search.toDate = toYmd(to)
      this.search.fromDate = toYmd(from)
      this.reload()
    },
    onManualDate() {
      this.periodPreset = ''
    },
    sortBy(col) {
      if (this.orderBy === col) {
        this.orderDir = this.orderDir === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.orderBy = col
        this.orderDir = 'DESC'
      }
      this.page = 1
      this.loadList()
    },
    sortMark(col) {
      if (this.orderBy !== col) return ''
      return this.orderDir === 'ASC' ? ' ▲' : ' ▼'
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1
        this.loadList()
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page += 1
        this.loadList()
      }
    },
    formatNum(v) {
      if (v === null || v === undefined || v === '') return '-'
      return Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })
    },
    formatInt(v) {
      if (v === null || v === undefined || v === '') return '-'
      return Number(v).toLocaleString()
    },
  },
}
</script>

<style scoped>
.print-volume-page {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px 24px;
}
.pv-hint {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}
.pv-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.preset-group {
  display: flex;
  gap: 4px;
}
.preset-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 14px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;
}
.preset-btn.on {
  border-color: #2563eb;
  color: #2563eb;
  background: #eff6ff;
}
.pv-control {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 13px;
}
.pv-btn {
  border: none;
  background: #2563eb;
  color: #fff;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 13px;
}
.pv-btn.outline {
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
}
.pv-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pv-kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 8px;
}
.pv-kpi {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}
.pv-kpi .label {
  font-size: 12px;
  color: #64748b;
}
.pv-kpi .value {
  margin-top: 4px;
  font-size: 18px;
  font-weight: 600;
}
.pv-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pv-tabs button {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 16px;
  padding: 4px 12px;
  cursor: pointer;
  font-size: 13px;
}
.pv-tabs button.active {
  border-color: #2563eb;
  color: #2563eb;
  background: #eff6ff;
}
.table-wrap {
  overflow: auto;
}
.pv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.pv-table th,
.pv-table td {
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 10px;
  text-align: left;
  white-space: nowrap;
}
.pv-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.pv-row-clickable {
  cursor: pointer;
}
.pv-row-clickable:hover {
  background: #f8fafc;
}
.cell-link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0c98fe;
  cursor: pointer;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cell-link:disabled {
  color: inherit;
  text-decoration: none;
  cursor: default;
}
.pv-cell-with-icon {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
}
.pv-drill-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 14px;
  padding: 3px 8px 3px 10px;
  font-size: 12px;
}
.pv-drill-clear {
  border: 0;
  background: transparent;
  color: #1d4ed8;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
}
.empty {
  text-align: center;
  color: #94a3b8;
  padding: 24px;
}
.pv-paging {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
}
</style>
